import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Divider from '@material-ui/core/Divider';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import ListItemText from '@material-ui/core/ListItemText';
import ListSubheader from '@material-ui/core/ListSubheader';
import Switch from '@material-ui/core/Switch';
import Typography from '@material-ui/core/Typography';
import {
    DefaultTimeInput,
    FirstDayOfWeek,
    InitialRange,
    RoundToNearest,
    usePreferencesState,
    useUpdatePreferences
} from '@tracker/shared/src/Preferences/PreferencesProvider';
import React from 'react';
import ListSelectItem from './ListSelectItem';

type Props = {
    open : boolean;
    onClose : () => void;
};

const PreferencesDialog : React.FC<Props> = ({open, onClose} : Props) => {
    const {preferences} = usePreferencesState();
    const updatePreferences = useUpdatePreferences();

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Preferences</DialogTitle>
            <DialogContent>
                <List disablePadding>
                    <ListSubheader disableGutters>
                        <Typography variant="h6">Locale</Typography>
                    </ListSubheader>
                    <ListItem disableGutters>
                        <ListItemText>Use AM/PM time</ListItemText>
                        <ListItemSecondaryAction>
                            <Switch
                                color="primary"
                                edge="end"
                                checked={preferences.locale.useAmPmTime}
                                onChange={(_event, checked) => updatePreferences({
                                    locale: {useAmPmTime: checked},
                                })}
                            />
                        </ListItemSecondaryAction>
                    </ListItem>
                    <Divider/>
                    <ListItem disableGutters>
                        <ListItemText>Use ISO date format</ListItemText>
                        <ListItemSecondaryAction>
                            <Switch
                                color="primary"
                                edge="end"
                                checked={preferences.locale.useIsoDate}
                                onChange={(_event, checked) => updatePreferences({
                                    locale: {useIsoDate: checked},
                                })}
                            />
                        </ListItemSecondaryAction>
                    </ListItem>
                    <Divider/>
                    <ListSelectItem<FirstDayOfWeek>
                        label="First day of week"
                        value={preferences.locale.firstDayOfWeek}
                        options={[
                            {value: 0, label: 'Sunday'},
                            {value: 1, label: 'Monday'},
                            {value: 2, label: 'Tuesday'},
                            {value: 3, label: 'Wednesday'},
                            {value: 4, label: 'Thursday'},
                            {value: 5, label: 'Friday'},
                            {value: 6, label: 'Saturday'},
                        ]}
                        onChange={value => updatePreferences({
                            locale: {
                                firstDayOfWeek: value
                            }
                        })}
                    />

                    <ListSubheader disableGutters>
                        <Typography variant="h6">Time Entry</Typography>
                    </ListSubheader>
                    <ListSelectItem<InitialRange>
                        label="Initial range"
                        value={preferences.timeEntry.initialRange}
                        options={[
                            {value: 'this-week', label: 'This week'},
                            {value: 'today', label: 'Today'},
                            {value: 'yesterday', label: 'Yesterday'},
                        ]}
                        onChange={value => updatePreferences({
                            timeEntry: {initialRange: value},
                        })}
                    />
                    <Divider/>
                    <ListSelectItem<DefaultTimeInput>
                        label="Input task duration"
                        value={preferences.timeEntry.defaultTimeInput}
                        options={[
                            {value: 'range', label: 'Range'},
                            {value: 'duration', label: 'Duration'},
                        ]}
                        onChange={value => updatePreferences({
                            timeEntry: {defaultTimeInput: value},
                        })}
                    />
                    <Divider/>
                    <ListSelectItem<RoundToNearest>
                        label="Round to nearest"
                        value={preferences.timeEntry.roundToNearest}
                        options={[
                            {value: 5, label: '5 Minutes'},
                            {value: 6, label: '6 Minutes'},
                            {value: 10, label: '10 Minutes'},
                            {value: 15, label: '15 Minutes'},
                        ]}
                        onChange={value => updatePreferences({
                            timeEntry: {roundToNearest: value},
                        })}
                    />
                    <Divider/>
                    <ListItem disableGutters>
                        <ListItemText>Input task hierarchy</ListItemText>
                        <ListItemSecondaryAction>
                            <Switch
                                color="primary"
                                edge="end"
                                checked={preferences.timeEntry.enterProjectAndComponent}
                                onChange={(_event, checked) => updatePreferences({
                                    timeEntry: {enterProjectAndComponent: checked},
                                })}
                            />
                        </ListItemSecondaryAction>
                    </ListItem>
                    <Divider/>
                    <ListItem disableGutters>
                        <ListItemText>Display projects and components</ListItemText>
                        <ListItemSecondaryAction>
                            <Switch
                                color="primary"
                                edge="end"
                                checked={preferences.timeEntry.showProjectAndComponent}
                                onChange={(_event, checked) => updatePreferences({
                                    timeEntry: {showProjectAndComponent: checked},
                                })}
                            />
                        </ListItemSecondaryAction>
                    </ListItem>
                    <Divider/>
                    <ListItem disableGutters>
                        <ListItemText>Display task IDs</ListItemText>
                        <ListItemSecondaryAction>
                            <Switch
                                color="primary"
                                edge="end"
                                checked={preferences.timeEntry.showTaskId}
                                onChange={(_event, checked) => updatePreferences({
                                    timeEntry: {showTaskId: checked},
                                })}
                            />
                        </ListItemSecondaryAction>
                    </ListItem>
                    <Divider/>
                    <ListItem disableGutters>
                        <ListItemText>Keep times on duplicate</ListItemText>
                        <ListItemSecondaryAction>
                            <Switch
                                color="primary"
                                edge="end"
                                checked={preferences.timeEntry.keepTimesOnDuplicate}
                                onChange={(_event, checked) => updatePreferences({
                                    timeEntry: {keepTimesOnDuplicate: checked},
                                })}
                            />
                        </ListItemSecondaryAction>
                    </ListItem>
                    <Divider/>
                    <ListItem disableGutters>
                        <ListItemText>Always use from date for new entries</ListItemText>
                        <ListItemSecondaryAction>
                            <Switch
                                color="primary"
                                edge="end"
                                checked={preferences.timeEntry.alwaysUseFromDateForNewEntries}
                                onChange={(_event, checked) => updatePreferences({
                                    timeEntry: {alwaysUseFromDateForNewEntries: checked},
                                })}
                            />
                        </ListItemSecondaryAction>
                    </ListItem>
                    <Divider/>
                    <ListItem disableGutters>
                        <ListItemText>Warn when hours for days of current week are under 80% of week target</ListItemText>
                        <ListItemSecondaryAction>
                            <Switch
                                color="primary"
                                edge="end"
                                checked={preferences.timeEntry.warnLowHoursThisWeek}
                                onChange={(_event, checked) => updatePreferences({
                                    timeEntry: {warnLowHoursThisWeek: checked},
                                })}
                            />
                        </ListItemSecondaryAction>
                    </ListItem>
                </List>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="primary">
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PreferencesDialog;
